import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, Target, Volume2 } from 'lucide-react';
import { englishContentService } from '../services/english/englishContentService';
import EnglishContentRenderer from '../components/english/EnglishContentRenderer';
import KaTeXRenderer from '../components/common/KaTeXRenderer';

interface EnglishLessonContent {
  id: string;
  title: string;
  vietnameseTitle: string;
  lop: number;
  chuong: number;
  bai: number;
  description: string;
  difficulty: string;
  estimatedTime: number;
  vocabulary: Array<{
    id: string;
    word: string;
    phonetic: string;
    meaning: string;
    example: string;
    exampleTranslation: string;
  }>;
  sentences: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
  paragraphs: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
}

const PhysicsEnglishPage: React.FC = () => {
  const { lop, chuong, bai } = useParams<{ lop: string; chuong: string; bai: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<EnglishLessonContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLesson = async () => {
      if (!lop || !chuong || !bai) {
        setError('Missing lesson parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const lessonData = await englishContentService.loadEnglishLesson(
          parseInt(lop),
          parseInt(chuong),
          parseInt(bai)
        );
        
        setLesson(lessonData);
      } catch (err) {
        console.error('Error loading lesson:', err);
        setError('Failed to load lesson content');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [lop, chuong, bai]);

  const handleBack = () => {
    navigate('/physics-english');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson content...</p>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Lesson Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The requested lesson could not be found.'}</p>
          <button
            onClick={handleBack}
            className="btn-primary inline-flex items-center px-6 py-3 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Physics English
          </button>
          
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                <KaTeXRenderer content={lesson.title} />
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Clock size={16} />
                  <span>{lesson.estimatedTime} phút</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Target size={16} />
                  <span className="capitalize">{lesson.difficulty}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <BookOpen size={16} />
                  <span>Lớp {lesson.lop} - Chương {lesson.chuong} - Bài {lesson.bai}</span>
                </div>
              </div>
            </div>
            <p className="text-lg text-gray-700 mb-2">
              <KaTeXRenderer content={lesson.vietnameseTitle} />
            </p>
            <p className="text-gray-600">
              <KaTeXRenderer content={lesson.description} />
            </p>
          </div>
        </div>

        {/* Content */}
        <EnglishContentRenderer content={lesson} />
      </div>
    </div>
  );
};

export default PhysicsEnglishPage;
